import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  CalendarDays, Clock, Coffee, Users, Download, 
  ChevronDown, Loader2, FileSpreadsheet, ChevronRight,
  MapPin, LogIn, LogOut, X
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Predefined colors for employees
const EMPLOYEE_COLORS = [
  'bg-amber-100 text-amber-800 border-amber-300',
  'bg-cyan-100 text-cyan-800 border-cyan-300',
  'bg-purple-100 text-purple-800 border-purple-300',
  'bg-lime-100 text-lime-800 border-lime-300',
  'bg-pink-100 text-pink-800 border-pink-300',
  'bg-blue-100 text-blue-800 border-blue-300',
  'bg-orange-100 text-orange-800 border-orange-300',
  'bg-teal-100 text-teal-800 border-teal-300',
  'bg-rose-100 text-rose-800 border-rose-300',
  'bg-indigo-100 text-indigo-800 border-indigo-300',
];

const TimesheetPage = () => {
  const [loading, setLoading] = useState(true);
  const [timesheetData, setTimesheetData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [endDate, setEndDate] = useState(endOfWeek(new Date(), { weekStartsOn: 1 }));
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [totals, setTotals] = useState({ totalHours: 0, breakHours: 0, regularHours: 0 });
  
  // Employee detail view state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeEntries, setEmployeeEntries] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Quick date range presets
  const datePresets = [
    { label: 'This Week', getRange: () => ({ start: startOfWeek(new Date(), { weekStartsOn: 1 }), end: endOfWeek(new Date(), { weekStartsOn: 1 }) }) },
    { label: 'Last Week', getRange: () => ({ start: startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 }), end: endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 }) }) },
    { label: 'This Month', getRange: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
    { label: 'Last 14 Days', getRange: () => ({ start: subDays(new Date(), 14), end: new Date() }) },
    { label: 'Last 30 Days', getRange: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  ];

  const fetchTimesheetData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      });
      
      if (selectedEmployees.length > 0) {
        selectedEmployees.forEach(id => params.append('employee_ids', id));
      }

      const res = await axios.get(`${API}/timesheet/summary?${params}`);
      setTimesheetData(res.data.employees || []);
      setTotals(res.data.totals || { totalHours: 0, breakHours: 0, regularHours: 0 });
    } catch (error) {
      console.error('Failed to fetch timesheet:', error);
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedEmployees]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchTimesheetData();
  }, [fetchTimesheetData]);

  const formatHours = (minutes) => {
    if (!minutes && minutes !== 0) return '00:00';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseISO(isoString), 'h:mm a');
    } catch {
      return '-';
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseISO(isoString), 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const openEmployeeDetail = async (employee, colorIndex) => {
    setSelectedEmployee({ ...employee, colorIndex });
    setDetailDialogOpen(true);
    setLoadingDetail(true);
    
    try {
      const params = new URLSearchParams({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      });
      
      const res = await axios.get(`${API}/timesheet/employee/${employee.id}?${params}`);
      setEmployeeEntries(res.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch employee detail:', error);
      toast.error('Failed to load time entries');
      setEmployeeEntries([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const applyDatePreset = (preset) => {
    const range = preset.getRange();
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const exportCSV = () => {
    const headers = ['Employee', 'Position', 'Total Hours', 'Break Hours', 'Regular Hours', 'Entries'];
    const rows = timesheetData.map(emp => [
      emp.name,
      emp.position || '-',
      formatHours(emp.totalMinutes),
      formatHours(emp.breakMinutes),
      formatHours(emp.regularMinutes),
      emp.entryCount
    ]);
    
    const csvContent = [
      `Timesheet: ${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Timesheet exported!');
  };

  const getEmployeeColor = (index) => {
    return EMPLOYEE_COLORS[index % EMPLOYEE_COLORS.length];
  };

  const displayedEmployees = employees.slice(0, 3);
  const remainingCount = employees.length - 3;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Timesheet</h1>
              <p className="text-sm sm:text-base text-slate-500">View employee hours for any date range</p>
            </div>
            <Button 
              variant="outline" 
              onClick={exportCSV}
              disabled={timesheetData.length === 0}
              className="gap-2"
              data-testid="export-timesheet-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>

          {/* Date Range & Employee Selection */}
          <Card className="border-0 shadow-md">
            <CardContent className="pt-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Date Range Pickers */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="gap-2 min-w-[140px] justify-start"
                        data-testid="start-date-btn"
                      >
                        <CalendarDays className="w-4 h-4" />
                        {format(startDate, 'MM/dd/yyyy')}
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => {
                          if (date) {
                            setStartDate(date);
                            setStartDateOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <span className="text-slate-500 font-medium">to:</span>

                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="gap-2 min-w-[140px] justify-start"
                        data-testid="end-date-btn"
                      >
                        <CalendarDays className="w-4 h-4" />
                        {format(endDate, 'MM/dd/yyyy')}
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => {
                          if (date) {
                            setEndDate(date);
                            setEndDateOpen(false);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Employee Avatars */}
                <div className="flex items-center gap-2 lg:ml-auto">
                  <div className="flex -space-x-2">
                    {displayedEmployees.map((emp, idx) => (
                      <button
                        key={emp.id}
                        onClick={() => toggleEmployeeSelection(emp.id)}
                        className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all ${
                          selectedEmployees.includes(emp.id) 
                            ? 'border-indigo-500 ring-2 ring-indigo-200' 
                            : 'border-white hover:border-indigo-300'
                        } ${getEmployeeColor(idx).split(' ')[0]}`}
                        title={emp.name}
                      >
                        {emp.name?.charAt(0).toUpperCase()}
                      </button>
                    ))}
                    {remainingCount > 0 && (
                      <div className="w-10 h-10 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
                        +{remainingCount}
                      </div>
                    )}
                  </div>
                  {selectedEmployees.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSelectedEmployees([])}
                      className="text-slate-500"
                    >
                      Clear filter
                    </Button>
                  )}
                </div>
              </div>

              {/* Quick Presets */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                {datePresets.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="ghost"
                    size="sm"
                    onClick={() => applyDatePreset(preset)}
                    className="text-slate-600 hover:text-indigo-600 hover:bg-indigo-50"
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Totals Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6 text-center">
                <Clock className="w-6 h-6 mx-auto text-indigo-600 mb-2" />
                <p className="text-slate-500 text-sm">Total Hours</p>
                <p className="text-2xl font-bold text-slate-900">{formatHours(totals.totalHours)}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6 text-center">
                <Coffee className="w-6 h-6 mx-auto text-amber-600 mb-2" />
                <p className="text-slate-500 text-sm">Break Hours</p>
                <p className="text-2xl font-bold text-slate-900">{formatHours(totals.breakHours)}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="pt-6 text-center">
                <FileSpreadsheet className="w-6 h-6 mx-auto text-green-600 mb-2" />
                <p className="text-slate-500 text-sm">Regular Hours</p>
                <p className="text-2xl font-bold text-slate-900">{formatHours(totals.regularHours)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Timesheet Table */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5" />
                Employee Hours
                <Badge variant="outline" className="ml-2">
                  {format(startDate, 'dd/MM/yyyy')} - {format(endDate, 'dd/MM/yyyy')}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : timesheetData.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="font-medium">No timesheet data found</p>
                  <p className="text-sm">Try selecting a different date range</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  {/* Table Header */}
                  <div className="grid grid-cols-5 gap-4 py-3 px-4 bg-slate-50 rounded-t-lg font-medium text-slate-600 text-sm sticky top-0">
                    <div>Employee</div>
                    <div className="text-center">Total Hours</div>
                    <div className="text-center">Break Hours</div>
                    <div className="text-center">Regular Hours</div>
                    <div></div>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y">
                    {timesheetData.map((employee, index) => (
                      <div 
                        key={employee.id} 
                        className="grid grid-cols-5 gap-4 py-4 px-4 hover:bg-indigo-50 transition-colors items-center cursor-pointer group"
                        data-testid={`timesheet-row-${employee.id}`}
                        onClick={() => openEmployeeDetail(employee, index)}
                      >
                        <div className="flex items-center gap-3">
                          <Badge 
                            className={`${getEmployeeColor(index)} border px-3 py-1`}
                          >
                            {employee.name}
                          </Badge>
                          {employee.position && (
                            <span className="text-xs text-slate-400 hidden md:inline">
                              {employee.position}
                            </span>
                          )}
                        </div>
                        <div className="text-center font-mono text-lg font-semibold text-slate-900">
                          {formatHours(employee.totalMinutes)}
                        </div>
                        <div className="text-center font-mono text-lg text-amber-600">
                          {formatHours(employee.breakMinutes)}
                        </div>
                        <div className="text-center font-mono text-lg font-semibold text-green-600">
                          {formatHours(employee.regularMinutes)}
                        </div>
                        <div className="text-right">
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 transition-colors inline-block" />
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Employee Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-3">
                {selectedEmployee && (
                  <Badge className={`${getEmployeeColor(selectedEmployee.colorIndex)} border px-3 py-1`}>
                    {selectedEmployee.name}
                  </Badge>
                )}
                <span className="text-slate-500 font-normal text-sm">
                  {format(startDate, 'dd/MM/yyyy')} - {format(endDate, 'dd/MM/yyyy')}
                </span>
              </DialogTitle>
              <DialogDescription>
                {selectedEmployee?.position || 'Employee'} • {employeeEntries.length} time entries
              </DialogDescription>
            </DialogHeader>
            
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : employeeEntries.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No time entries for this period</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0" style={{ maxHeight: 'calc(85vh - 140px)' }}>
                <div className="space-y-3 pb-4">
                  {employeeEntries.map((entry, idx) => (
                    <div 
                      key={entry.id || idx}
                      className="bg-slate-50 rounded-lg p-4 space-y-3"
                    >
                      {/* Date Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-slate-900">
                            {formatDate(entry.clock_in)}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-indigo-600">
                          {formatHours(entry.regularMinutes || (entry.workedMinutes - (entry.breakMinutes || 0)))} net
                        </Badge>
                      </div>
                      
                      {/* Time Details */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <LogIn className="w-4 h-4 text-green-600" />
                          <div>
                            <p className="text-slate-500 text-xs">Clock In</p>
                            <p className="font-medium">{formatTime(entry.clock_in)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <LogOut className="w-4 h-4 text-red-600" />
                          <div>
                            <p className="text-slate-500 text-xs">Clock Out</p>
                            <p className="font-medium">{formatTime(entry.clock_out)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-500" />
                          <div>
                            <p className="text-slate-500 text-xs">Gross Hours</p>
                            <p className="font-medium">{formatHours(entry.workedMinutes)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Coffee className="w-4 h-4 text-amber-600" />
                          <div>
                            <p className="text-slate-500 text-xs">Breaks</p>
                            <p className="font-medium text-amber-600">-{formatHours(entry.breakMinutes)}</p>
                          </div>
                        </div>
                        {entry.office_name && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-blue-600" />
                            <div>
                              <p className="text-slate-500 text-xs">Location</p>
                              <p className="font-medium truncate">{entry.office_name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Break Details */}
                      {entry.breaks && entry.breaks.length > 0 && (
                        <div className="pt-2 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2">Break Details:</p>
                          <div className="flex flex-wrap gap-2">
                            {entry.breaks.map((br, brIdx) => (
                              <Badge key={brIdx} variant="outline" className="text-xs">
                                {formatTime(br.start)} - {formatTime(br.end)}
                                {br.type && ` (${br.type})`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default TimesheetPage;
